import {
  Alert,
  Button,
  Field,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  parseDecimal,
  useToast,
} from '@catering/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type JSX, useState } from 'react';

import { bulkUpdateContractPrices, type BulkPriceChange } from '../../../contracts/api';
import { mapPriceError } from '../../../contracts/contract-api-errors';
import { formatPriceCents } from '../../../contracts/format-price';

const KIND_LABELS: Record<'SERVICE_TYPE' | 'MENU', string> = {
  SERVICE_TYPE: 'Servicio',
  MENU: 'Plato',
};

export interface BulkPriceUpdateModalProps {
  contractId: string;
  onClose: () => void;
  onApplied: () => void;
}

/**
 * `contract-prices/spec.md` → "Actualización masiva de precios por
 * porcentaje", "Confirmación requerida antes de aplicar cambio masivo":
 * `preview: true` first, a table of `oldPriceCents` → `newPriceCents`,
 * then an explicit second action (`preview: false`) — never the same
 * click.
 */
export function BulkPriceUpdateModal({
  contractId,
  onClose,
  onApplied,
}: BulkPriceUpdateModalProps): JSX.Element {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [percentage, setPercentage] = useState('');
  const [changes, setChanges] = useState<BulkPriceChange[] | null>(null);

  const previewMutation = useMutation({
    mutationFn: (value: number) => bulkUpdateContractPrices(contractId, value, true),
    onSuccess: (result) => setChanges(result.changes),
  });

  const applyMutation = useMutation({
    mutationFn: (value: number) => bulkUpdateContractPrices(contractId, value, false),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['contract-prices', contractId] });
      toast('success', 'Precios actualizados');
      onApplied();
      onClose();
    },
  });

  const parsedPercentage = parseDecimal(percentage);
  const percentageValid = parsedPercentage !== null;
  const percentageInvalid = percentage.trim() !== '' && !percentageValid;

  return (
    <Modal open onClose={onClose} title="Actualización masiva de precios" className="max-w-lg">
      <div className="flex flex-col gap-4">
        {previewMutation.isError ? (
          <Alert tone="danger">{mapPriceError(previewMutation.error)}</Alert>
        ) : null}
        {applyMutation.isError ? (
          <Alert tone="danger">{mapPriceError(applyMutation.error)}</Alert>
        ) : null}

        <Field
          label="Porcentaje"
          name="percentage"
          hint="Positivo para aumentar, negativo para descontar. Ejemplo: 10,5"
          {...(percentageInvalid ? { error: 'Ingresá un porcentaje válido, por ejemplo 10 o -5,5' } : {})}
          value={percentage}
          onChange={(event) => {
            setPercentage(event.target.value);
            setChanges(null);
          }}
        />

        {changes !== null ? (
          <Table>
            <caption className="sr-only">Previsualización de cambios</caption>
            <thead>
              <tr>
                <TableHeader>Tipo</TableHeader>
                <TableHeader>Nombre</TableHeader>
                <TableHeader>Precio actual</TableHeader>
                <TableHeader>Precio nuevo</TableHeader>
              </tr>
            </thead>
            <TableBody>
              {changes.map((change) => (
                <TableRow key={`${change.kind}-${change.targetId}`}>
                  <TableCell>{KIND_LABELS[change.kind]}</TableCell>
                  <TableCell>{change.targetName}</TableCell>
                  <TableCell>{formatPriceCents(change.oldPriceCents)}</TableCell>
                  <TableCell className="font-medium text-slate-900">
                    {formatPriceCents(change.newPriceCents)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}

        <div className="flex items-center gap-3">
          <Button
            type="button"
            loading={previewMutation.isPending}
            disabled={!percentageValid}
            onClick={() => previewMutation.mutate(parsedPercentage ?? 0)}
          >
            Previsualizar
          </Button>

          {changes !== null ? (
            <Button
              type="button"
              variant="destructive"
              loading={applyMutation.isPending}
              onClick={() => applyMutation.mutate(parsedPercentage ?? 0)}
            >
              Confirmar y aplicar
            </Button>
          ) : null}

          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
